// POS V2 cart-line domain helpers.
// - orderItemToCartLine mirrors admin's `orderItemToCartItem` (src/utils/orders.utils.ts)
//   but produces the IPosCartLine shape (comment round-trips VERBATIM: '' vs null
//   participates in the backend item diff).
// - getOrderEditRequiresCancel mirrors backend `OrderUtils.getOrderItemsDiff` +
//   the `didRemoveItems` escalation of `V2OrdersService.update`.
// - shouldEnforceStrictOutsideZoneForDraft ported from cartadigital-pos
//   app/composables/usePosOrderDrafts.ts.
import type { IOrderItem } from '@/interfaces/IOrderItem';
import type { ISelectedOptionsMap } from './item-options-validation';
import type { IPosCartLine, TPosDraftMode } from './pos-types';

/**
 * Largest quantity a cart line accepts. `order_items.print_t/print_c/print_e`
 * are MySQL smallint, so a bigger amount could never be marked fully printed
 * and would re-queue its comanda on every print catch-up.
 */
export const POS_MAX_LINE_QUANTITY = 32767;

/**
 * Order-independent signature from a selected options map
 * (`Record<optionId, Record<optionItemId, val>>`). Only positive values count.
 */
export function buildSelectedOptionsSignature(
  selectedOptions: ISelectedOptionsMap
): string {
  const parts: string[] = [];
  for (const optionIdKey in selectedOptions) {
    const inner = selectedOptions[optionIdKey];
    if (!inner) continue;

    for (const optionItemIdKey in inner) {
      const optionItemId = Number(optionItemIdKey);
      if (!Number.isFinite(optionItemId)) continue;

      const rawVal = inner[optionItemIdKey];
      const val = typeof rawVal === 'number' ? rawVal : 0;
      if (val > 0) parts.push(`${optionItemId}_${val}`);
    }
  }
  parts.sort();
  return parts.join('|');
}

/**
 * Same signature, built from a persisted order item's options rows.
 */
export function buildOrderItemOptionsSignature(orderItem: IOrderItem): string {
  const parts: string[] = [];
  for (const opt of orderItem.options ?? []) {
    if (opt.val > 0) parts.push(`${opt.optionItemId}_${opt.val}`);
  }
  parts.sort();
  return parts.join('|');
}

/**
 * Maps a persisted order item onto a draft cart line.
 *
 * - `userComment` is preserved verbatim (`null` stays `null`, `''` stays `''`):
 *   the backend diff compares comments strictly, so normalizing here would make
 *   a no-op save cancel-and-recreate every line (and demand cancel permission).
 * - `index` is the line's identity for the backend diff — kept as stored.
 */
export function orderItemToCartLine(orderItem: IOrderItem): IPosCartLine {
  const optionValues: ISelectedOptionsMap = {};

  for (const option of orderItem.options ?? []) {
    const optionId = option.optionItem?.option?.id;
    if (typeof optionId !== 'number') continue;

    const optionKey = String(optionId);
    if (!optionValues[optionKey]) optionValues[optionKey] = {};
    optionValues[optionKey][String(option.optionItem!.id)] = option.val;
  }

  return {
    index: orderItem.index ?? 0,
    itemId: orderItem.itemId,
    quantity: orderItem.amount,
    unitPrice: orderItem.price,
    totalPrice: orderItem.total,
    userComment: orderItem.comment ?? null,
    optionValues,
  };
}

/**
 * Mirrors the backend escalation predicate of `V2OrdersService.update`:
 * cancel permission (`PermissionType.OrdersCancel`) is required when any
 * ACTIVE persisted line was
 *  (a) removed (its index has no counterpart in the new cart),
 *  (b) quantity-decreased, or
 *  (c) changed at an existing index in itemId, comment, or options — the
 *      backend classifies that as cancel-and-re-add (`didRemoveItems`).
 * Pure quantity increases and brand-new indexes are exempt.
 */
export function getOrderEditRequiresCancel(
  originalItems: readonly IOrderItem[],
  cartLines: readonly IPosCartLine[]
): boolean {
  const linesByIndex = new Map<number, IPosCartLine>();
  for (const line of cartLines) {
    linesByIndex.set(line.index, line);
  }

  for (const original of originalItems) {
    if (original.canceledAt) continue;

    const line = linesByIndex.get(original.index ?? 0);

    // (a) removed
    if (!line) return true;

    // (c) changed identity/content at the same index
    if (line.itemId !== original.itemId) return true;
    if ((line.userComment ?? null) !== (original.comment ?? null)) return true;
    if (
      buildSelectedOptionsSignature(line.optionValues) !==
      buildOrderItemOptionsSignature(original)
    ) {
      return true;
    }

    // (b) quantity decrease
    if (line.quantity < original.amount) return true;
  }

  return false;
}

export interface IRekeyedCartLines {
  cartLines: IPosCartLine[];
  nextIndex: number;
}

/**
 * Indexes are the update diff's join key and the backend rejects a different
 * item at an index the order already uses (canceled rows included). Lines that
 * collide that way (two tabs restored the same draft and each added its own
 * line) move to fresh indexes; every other line keeps its index so quantity
 * changes still diff as such.
 */
export function rekeyCollidingCartLines(
  cartLines: readonly IPosCartLine[],
  persistedItems: readonly IOrderItem[],
  nextIndex: number
): IRekeyedCartLines {
  const persistedItemIdByIndex = new Map<number, number>();
  for (const item of persistedItems) {
    persistedItemIdByIndex.set(item.index ?? 0, item.itemId);
  }

  let next = nextIndex;
  const rekeyed = cartLines.map((line) => {
    const persistedItemId = persistedItemIdByIndex.get(line.index);
    if (persistedItemId === undefined || persistedItemId === line.itemId) {
      return line;
    }
    return { ...line, index: next++ };
  });

  return { cartLines: rekeyed, nextIndex: next };
}

/** A delivery coordinate is only meaningful as a finite number. */
export function normalizeDeliveryCoord(
  value: number | null | undefined
): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function isDeliveryCoordsChanged(params: {
  baselineLat: number | null | undefined;
  baselineLng: number | null | undefined;
  nextLat: number | null | undefined;
  nextLng: number | null | undefined;
}): boolean {
  return (
    normalizeDeliveryCoord(params.baselineLat) !==
      normalizeDeliveryCoord(params.nextLat) ||
    normalizeDeliveryCoord(params.baselineLng) !==
      normalizeDeliveryCoord(params.nextLng)
  );
}

/**
 * Strict outside-zone enforcement only applies when the user is actually
 * choosing/changing delivery coords — never for untouched edits of existing
 * orders (zoning rules may have changed since creation) nor for view tabs.
 */
export function shouldEnforceStrictOutsideZoneForDraft(params: {
  mode: TPosDraftMode;
  baselineLat: number | null | undefined;
  baselineLng: number | null | undefined;
  nextLat: number | null | undefined;
  nextLng: number | null | undefined;
}): boolean {
  if (params.mode === 'create') return true;
  if (params.mode === 'edit') {
    return isDeliveryCoordsChanged({
      baselineLat: params.baselineLat,
      baselineLng: params.baselineLng,
      nextLat: params.nextLat,
      nextLng: params.nextLng,
    });
  }
  return false;
}
